import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  ArrowLeft, Building2, MapPin, User,
  FileText, CheckCircle2, Clock, AlertCircle, Mail, Phone,
  Activity, Edit, ChevronRight, Zap, Target,
  RotateCcw, Sparkles, RefreshCw, X, Copy, Download, Trash2,
  Paperclip, Upload, FileIcon, Image, Save, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';
import { generateSummaryPDF, downloadPDF } from '../utils/pdfExport';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-sky-500' },
  under_review: { label: 'Under Review', color: 'bg-amber-500' },
  decision_made: { label: 'Decision Made', color: 'bg-violet-500' },
  quoted: { label: 'Quoted', color: 'bg-emerald-500' },
  won: { label: 'Won', color: 'bg-green-600' },
  lost: { label: 'Lost', color: 'bg-red-500' },
  archived: { label: 'Archived', color: 'bg-slate-400' },
};

const PROJECT_TYPE_LABELS = {
  cat_a: 'CAT A',
  cat_b: 'CAT B',
  refurbishment: 'Refurbishment',
  new_build: 'New Build',
  residential: 'Residential',
};

const CLIENT_TYPE_LABELS = {
  contractor: 'Main Contractor',
  project_manager: 'Project Manager',
  quantity_surveyor: 'Quantity Surveyor',
  developer: 'Developer',
  landlord: 'Landlord',
  architect: 'Architect',
  end_user: 'End User / Tenant',
  direct: 'Direct Client',
  repeat: 'Repeat Client',
};

// Delivery authority labels - kept for future UI display
 
const _DELIVERY_AUTHORITY_LABELS = {
  confirmed: 'Confirmed',
  likely: 'Likely',
  unknown: 'Unknown',
  has_authority: 'Has Authority',
  no_authority: 'No Authority',
};

export const EnquiryDetailPage = () => {
  const { enquiryId } = useParams();
  const navigate = useNavigate();
  const { user, authFetch } = useAuth();
  const [enquiry, setEnquiry] = useState(null);
  const [decision, setDecision] = useState(null);
  const [quote, setQuote] = useState(null);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Summary and Reset modals
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Attachments state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('general');
  
  // Edit Enquiry modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    project_name: '',
    location: '',
    estimated_value: '',
    description: '',
    project_stage: '',
    has_programme_summary: false,
    has_scope_summary: false,
    decision_maker_name: '',
    client_type: '',
    delivery_authority: ''
  });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryId]);

  const loadData = async () => {
    try {
      // Load enquiry
      const enquiryRes = await authFetch(`/enquiries/${enquiryId}`);
      if (enquiryRes.ok) {
        const data = await enquiryRes.json();
        setEnquiry(data);
      }
      
      // Load decision
      try {
        const decisionRes = await authFetch(`/decisions/enquiry/${enquiryId}`);
        if (decisionRes.ok) {
          const data = await decisionRes.json();
          setDecision(data);
        }
      } catch {
        // Decision may not exist yet
      }
      
      // Load quote
      try {
        const quoteRes = await authFetch(`/quotes/enquiry/${enquiryId}`);
        if (quoteRes.ok) {
          const data = await quoteRes.json();
          setQuote(data);
        }
      } catch {
        // Quote may not exist yet
      }
      
      // Load activities
      const actRes = await authFetch(`/actions/entity/${enquiryId}`);
      if (actRes.ok) {
        const data = await actRes.json();
        setActivities(data.actions || []);
      }
      
      // Load attachments
      try {
        const attachRes = await authFetch(`/enquiries/${enquiryId}/attachments`);
        if (attachRes.ok) {
          const data = await attachRes.json();
          setAttachments(data.attachments || []);
        }
      } catch {
        // Attachments may not be available
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await authFetch(`/enquiries/${enquiryId}/summary`);
      if (res.ok) {
        const data = await res.json();
        setSummaryData(data);
        setShowSummaryModal(true);
      } else {
        toast.error('Failed to generate summary');
      }
    } catch (err) {
      toast.error('Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const resetDecision = async () => {
    setResetLoading(true);
    try {
      const res = await authFetch(`/enquiries/${enquiryId}/reset-decision`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Decision reset successfully. You can now re-run the decision engine.');
        setDecision(null);
        setShowResetModal(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reset decision');
      }
    } catch (err) {
      toast.error('Failed to reset decision');
    } finally {
      setResetLoading(false);
    }
  };

  const updateQuoteOutcome = async (outcome) => {
    try {
      const res = await authFetch(`/enquiries/${enquiryId}/quote-outcome`, {
        method: 'PATCH',
        body: { outcome }
      });
      if (res.ok) {
        const data = await res.json();
        setEnquiry(prev => ({ ...prev, quote_outcome: outcome, status: data.status }));
        toast.success(`Quote marked as ${outcome}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update outcome');
      }
    } catch (err) {
      toast.error('Failed to update outcome');
    }
  };

  // Edit Enquiry functions
  const openEditModal = () => {
    if (enquiry) {
      setEditForm({
        client_name: enquiry.client_name || '',
        client_email: enquiry.client_email || '',
        client_phone: enquiry.client_phone || '',
        client_company: enquiry.client_company || '',
        project_name: enquiry.project_name || '',
        location: enquiry.location || '',
        estimated_value: enquiry.estimated_value || '',
        description: enquiry.description || '',
        project_stage: enquiry.project_stage || '',
        has_programme_summary: enquiry.has_programme_summary || false,
        has_scope_summary: enquiry.has_scope_summary || false,
        decision_maker_name: enquiry.decision_maker_name || '',
        client_type: enquiry.client_type || '',
        delivery_authority: enquiry.delivery_authority || ''
      });
      setShowEditModal(true);
    }
  };

  const handleEditSubmit = async (rerunDecision = false) => {
    setEditLoading(true);
    try {
      // Build update object with only changed fields
      const updates = {};
      Object.keys(editForm).forEach(key => {
        const currentValue = enquiry[key];
        const newValue = editForm[key];
        // Check if value changed
        if (newValue !== currentValue && newValue !== '' && newValue !== null) {
          updates[key] = newValue;
        }
      });
      
      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setEditLoading(false);
        return;
      }
      
      const res = await authFetch(`/enquiries/${enquiryId}`, {
        method: 'PATCH',
        body: updates
      });
      
      if (res.ok) {
        const data = await res.json();
        setEnquiry(data.enquiry);
        setShowEditModal(false);
        toast.success('Enquiry updated successfully');
        
        // If clarity fields were updated and user wants to re-run decision
        if (rerunDecision && data.can_rerun_decision) {
          // Reset existing decision first
          if (decision) {
            await resetDecision();
          }
          // Navigate to decision engine
          navigate(`/enquiry/${enquiryId}/decision`);
        } else if (data.can_rerun_decision && data.clarity_fields_updated?.length > 0) {
          // Offer to re-run decision
          toast.info(
            <div>
              <p className="font-medium">Project clarity updated!</p>
              <p className="text-sm">You can now re-run the Decision Engine.</p>
            </div>,
            { duration: 5000 }
          );
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update enquiry');
      }
    } catch (err) {
      toast.error('Failed to update enquiry');
    } finally {
      setEditLoading(false);
    }
  };

  const copySummaryToClipboard = () => {
    if (summaryData?.text_summary) {
      navigator.clipboard.writeText(summaryData.text_summary);
      toast.success('Summary copied to clipboard');
    }
  };

  // Attachment functions
  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadCategory);
      
      const token = localStorage.getItem('opsflow_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/${enquiryId}/attachments/upload?category=${uploadCategory}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setAttachments(prev => [data, ...prev]);
        toast.success('File uploaded successfully');
        setShowUploadModal(false);
        setUploadCategory('general');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload file');
      }
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const token = localStorage.getItem('opsflow_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${attachment.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Failed to download file');
      }
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      const res = await authFetch(`/attachments/${attachmentId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        setAttachments(prev => prev.filter(a => a.id !== attachmentId));
        toast.success('File deleted');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete file');
      }
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const deleteEnquiry = async () => {
    setDeleteLoading(true);
    try {
      const res = await authFetch(`/enquiries/${enquiryId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Enquiry deleted');
        navigate('/enquiries');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete enquiry');
      }
    } catch (err) {
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleteLoading(false);
    }
  };

  const exportSummaryToPDF = async () => {
    try {
      const pdfDoc = await generateSummaryPDF(summaryData, enquiry);
      const filename = `Summary_${enquiry?.reference_number || 'enquiry'}_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadPDF(pdfDoc, filename);
      toast.success('Summary exported to PDF');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  if (!enquiry) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Enquiry not found</p>
          <Button onClick={() => navigate('/enquiries')} className="mt-4">
            Back to Enquiries
          </Button>
        </div>
      </MainLayout>
    );
  }

  const status = STATUS_CONFIG[enquiry.status] || STATUS_CONFIG.new;

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="enquiry-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/enquiries')}
              className="-ml-3 mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Enquiries
            </Button>
            
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-sm font-mono text-slate-500">
                {enquiry.reference_number}
              </span>
              <span className={`chip ${status.color} text-white text-xs`}>
                {status.label}
              </span>
              {enquiry.project_type && (
                <span className="chip chip-primary text-xs hidden sm:inline-flex">
                  {PROJECT_TYPE_LABELS[enquiry.project_type] || enquiry.project_type}
                </span>
              )}
            </div>
            
            <h1 className="font-heading text-xl md:text-3xl font-bold text-[#1e3a5f] line-clamp-2">
              {enquiry.project_name}
            </h1>
          </div>
          
          <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
            {/* Generate Summary Button */}
            <Button
              onClick={generateSummary}
              variant="outline"
              disabled={summaryLoading}
              className="rounded-full text-xs md:text-sm px-3 md:px-4"
              data-testid="generate-summary-btn"
            >
              {summaryLoading ? (
                <RefreshCw className="w-4 h-4 md:mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 md:mr-2" />
              )}
              <span className="hidden md:inline">Summary</span>
            </Button>
            
            {/* Reset Decision Button - Only for Director when decision exists and no quote */}
            {decision && !quote && user?.role === 'director' && (
              <Button
                onClick={() => setShowResetModal(true)}
                variant="outline"
                className="rounded-full text-amber-600 border-amber-300 hover:bg-amber-50 text-xs md:text-sm px-3 md:px-4"
                data-testid="reset-decision-btn"
              >
                <RotateCcw className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Reset</span>
              </Button>
            )}
            
            {/* Run Decision - Show when no decision OR after reset */}
            {!decision && (
              <Button
                onClick={() => navigate(`/enquiry/${enquiryId}/decision`)}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full text-xs md:text-sm px-3 md:px-4"
                data-testid="run-decision-btn"
              >
                <Zap className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Run Decision</span>
                <span className="md:hidden">Decide</span>
              </Button>
            )}
            
            {/* Create Quote - Only when decision allows */}
            {decision?.can_create_quote && !quote && (
              <Button
                onClick={() => navigate(`/enquiry/${enquiryId}/quote`)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs md:text-sm px-3 md:px-4"
                data-testid="create-quote-btn"
              >
                <FileText className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Create Quote</span>
                <span className="md:hidden">Quote</span>
              </Button>
            )}
            
            {/* Delete Enquiry - Director only */}
            {user?.role === 'director' && !quote && (
              <Button
                onClick={() => setShowDeleteModal(true)}
                variant="outline"
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50 px-3"
                data-testid="delete-enquiry-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 pb-1">
            <TabsList className="bg-slate-100 p-1 rounded-xl inline-flex min-w-max gap-1">
              <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm px-3 py-2 whitespace-nowrap">
                Overview
              </TabsTrigger>
              <TabsTrigger value="decision" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm px-3 py-2 whitespace-nowrap">
                Decision
                {decision && <CheckCircle2 className="w-3 h-3 ml-1 text-emerald-500" />}
              </TabsTrigger>
              <TabsTrigger value="activity" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm px-3 py-2 whitespace-nowrap">
                Activity
              </TabsTrigger>
              <TabsTrigger value="quote" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm px-3 py-2 whitespace-nowrap">
                Quote
                {quote && <CheckCircle2 className="w-3 h-3 ml-1 text-emerald-500" />}
              </TabsTrigger>
              <TabsTrigger value="attachments" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm px-3 py-2 whitespace-nowrap">
                <Paperclip className="w-3 h-3 mr-1" />
                Files
                {attachments.length > 0 && (
                  <span className="ml-1 bg-slate-200 text-slate-600 text-xs px-1.5 py-0.5 rounded-full">
                    {attachments.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Client Info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Client
                  </h3>
                  {user?.role === 'director' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={openEditModal}
                      className="text-slate-500 hover:text-[#1e3a5f] -mr-2"
                      data-testid="edit-enquiry-btn"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium text-slate-800">{enquiry.client_name}</p>
                  </div>
                  {enquiry.client_company && (
                    <div>
                      <p className="text-sm text-slate-500">Company</p>
                      <p className="font-medium text-slate-800">{enquiry.client_company}</p>
                    </div>
                  )}
                  {enquiry.client_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${enquiry.client_email}`} className="text-[#1e3a5f] hover:underline">
                        {enquiry.client_email}
                      </a>
                    </div>
                  )}
                  {enquiry.client_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${enquiry.client_phone}`} className="text-[#1e3a5f] hover:underline">
                        {enquiry.client_phone}
                      </a>
                    </div>
                  )}
                  {enquiry.client_type && (
                    <div>
                      <p className="text-sm text-slate-500">Client Type</p>
                      <p className="font-medium text-slate-800">
                        {CLIENT_TYPE_LABELS[enquiry.client_type] || enquiry.client_type.replace('_', ' ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Project
                </h3>
                <div className="space-y-3">
                  {enquiry.location && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <p className="text-slate-800">{enquiry.location}</p>
                    </div>
                  )}
                  {enquiry.sqft && (
                    <div>
                      <p className="text-sm text-slate-500">Size</p>
                      <p className="font-medium text-slate-800">{enquiry.sqft.toLocaleString('en-GB')} sq ft</p>
                    </div>
                  )}
                  {enquiry.mep_disciplines?.length > 0 && (
                    <div>
                      <p className="text-sm text-slate-500 mb-2">MEP Disciplines</p>
                      <div className="flex flex-wrap gap-1">
                        {enquiry.mep_disciplines.map(d => (
                          <span key={d} className="chip chip-default text-xs capitalize">
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {enquiry.project_stage && (
                    <div>
                      <p className="text-sm text-slate-500">Stage</p>
                      <p className="font-medium text-slate-800 capitalize">
                        {enquiry.project_stage.replace('_', ' ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Value & Timeline */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Value & Timeline
                </h3>
                <div className="space-y-3">
                  {enquiry.estimated_value && (
                    <div>
                      <p className="text-sm text-slate-500">Estimated Value</p>
                      <p className="font-heading text-2xl font-bold text-[#1e3a5f]">
                        £{enquiry.estimated_value.toLocaleString('en-GB')}
                      </p>
                    </div>
                  )}
                  {enquiry.timeline_start && (
                    <div>
                      <p className="text-sm text-slate-500">Start Date</p>
                      <p className="font-medium text-slate-800">
                        {new Date(enquiry.timeline_start).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                  {enquiry.timeline_end && (
                    <div>
                      <p className="text-sm text-slate-500">End Date</p>
                      <p className="font-medium text-slate-800">
                        {new Date(enquiry.timeline_end).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500">Created</p>
                    <p className="font-medium text-slate-800">
                      {new Date(enquiry.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {enquiry.description && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
                  Description
                </h3>
                <p className="text-slate-700 whitespace-pre-wrap">{enquiry.description}</p>
              </div>
            )}

            {/* Project Clarity */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
                Project Clarity
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl ${enquiry.project_stage ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {enquiry.project_stage ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm font-medium">Stage Known</span>
                  </div>
                  <p className="text-sm text-slate-500 capitalize">
                    {enquiry.project_stage?.replace('_', ' ') || 'Unknown'}
                  </p>
                </div>
                
                <div className={`p-4 rounded-xl ${enquiry.has_programme_summary ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {enquiry.has_programme_summary ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm font-medium">Programme</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {enquiry.has_programme_summary ? 'Available' : 'Not Available'}
                  </p>
                </div>
                
                <div className={`p-4 rounded-xl ${enquiry.has_scope_summary ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {enquiry.has_scope_summary ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm font-medium">Scope</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {enquiry.has_scope_summary ? 'Available' : 'Not Available'}
                  </p>
                </div>
                
                <div className={`p-4 rounded-xl ${enquiry.decision_maker_name ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {enquiry.decision_maker_name ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm font-medium">Decision Maker</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {enquiry.decision_maker_name || 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Decision Tab */}
          <TabsContent value="decision" className="space-y-6">
            {decision ? (
              <>
                {/* Decision Outcome Banner */}
                <div className={`rounded-2xl p-6 ${
                  decision.outcome === 'quote' ? 'bg-emerald-50 border border-emerald-200' :
                  decision.outcome === 'quote_approval_required' ? 'bg-sky-50 border border-sky-200' :
                  decision.outcome === 'do_not_quote' ? 'bg-red-50 border border-red-200' :
                  decision.outcome === 'do_not_quote_yet' ? 'bg-amber-50 border border-amber-200' :
                  'bg-slate-50 border border-slate-200'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                      decision.outcome === 'quote' ? 'bg-emerald-500' :
                      decision.outcome === 'quote_approval_required' ? 'bg-sky-500' :
                      decision.outcome === 'do_not_quote' ? 'bg-red-500' :
                      decision.outcome === 'do_not_quote_yet' ? 'bg-amber-500' :
                      'bg-slate-500'
                    }`}>
                      {decision.can_create_quote ? (
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      ) : (
                        <AlertCircle className="w-8 h-8 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-[#1e3a5f]">
                        {decision.outcome_label}
                      </h3>
                      <p className="text-slate-600">
                        {decision.step_results?.filter(s => s.passed).length || 0} of {decision.step_results?.length || 0} steps passed • 
                        Decided {new Date(decision.decided_at).toLocaleDateString('en-GB')}
                      </p>
                      {decision.is_override && (
                        <p className="text-sm text-amber-600 mt-1">
                          Overridden by Director: {decision.override_reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step Results */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
                    Decision Steps
                  </h3>
                  <div className="space-y-3">
                    {decision.step_results?.map((step, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-xl border ${
                          step.passed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {step.passed ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-600" />
                            )}
                            <span className="font-medium">
                              Step {step.step_number}: {step.step_name}
                            </span>
                          </div>
                          <span className={`text-sm font-medium ${
                            step.passed ? 'text-emerald-600' : 'text-red-600'
                          }`}>
                            {step.passed ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                        {step.reason && (
                          <p className="text-sm text-slate-600 mt-2 ml-8">
                            {step.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Update & Re-evaluate Section - Show when decision is Pause or Do Not Quote */}
                {(decision.outcome === 'pause' || decision.outcome === 'do_not_quote' || decision.outcome === 'do_not_quote_yet') && (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Info className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-amber-800 mb-2">
                          Missing Information?
                        </h3>
                        <p className="text-amber-700 text-sm mb-4">
                          If you have received additional project clarity (client type, programme details, 
                          decision maker information), you can update the enquiry and re-run the decision engine.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            onClick={openEditModal}
                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
                            data-testid="update-reevaluate-btn"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Update Enquiry Details
                          </Button>
                          <Button
                            onClick={async () => {
                              await resetDecision();
                              navigate(`/enquiry/${enquiryId}/decision`);
                            }}
                            variant="outline"
                            className="border-amber-300 text-amber-700 hover:bg-amber-100 rounded-xl"
                            data-testid="rerun-decision-btn"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Re-run Decision Engine
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-700 mb-2">
                  No Decision Yet
                </h3>
                <p className="text-slate-500 mb-6">
                  Run the decision engine to determine if you should quote this enquiry
                </p>
                <Button
                  onClick={() => navigate(`/enquiry/${enquiryId}/decision`)}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Run Decision Engine
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
                Activity Timeline
              </h3>
              {activities.length > 0 ? (
                <div className="space-y-4">
                  {activities.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Activity className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-700">
                          <span className="font-medium capitalize">
                            {activity.action_type.replace('_', ' ')}
                          </span>
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(activity.timestamp).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No activity recorded yet</p>
              )}
            </div>
          </TabsContent>

          {/* Quote Tab */}
          <TabsContent value="quote" className="space-y-4 md:space-y-6">
            {quote ? (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-500 truncate">{quote.quote_number}</p>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-[#1e3a5f]">
                        £{quote.total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </h3>
                    </div>
                    <span className={`chip self-start sm:self-auto whitespace-nowrap ${
                      quote.status === 'approved' ? 'bg-emerald-500' :
                      quote.status === 'pending_approval' ? 'bg-amber-500' :
                      quote.status === 'sent' ? 'bg-sky-500' :
                      quote.status === 'accepted' ? 'bg-green-600' :
                      quote.status === 'rejected' ? 'bg-red-500' :
                      'bg-slate-500'
                    } text-white text-xs md:text-sm`}>
                      {quote.status?.replace('_', ' ')}
                    </span>
                    {quote.client_response && (
                      <span className={`chip self-start sm:self-auto whitespace-nowrap text-xs md:text-sm ${
                        quote.client_response === 'approved' ? 'bg-emerald-600 text-white' : 'bg-slate-400 text-white'
                      }`}>
                        Client: {quote.client_response === 'approved' ? 'Approved' : 'Not Approved'}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-slate-100 gap-2">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-right">£{quote.subtotal?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100 gap-2">
                      <span className="text-slate-500">VAT ({quote.vat_rate}%)</span>
                      <span className="text-right">£{quote.vat_amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-2 font-semibold gap-2">
                      <span>Total</span>
                      <span className="text-right">£{quote.total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => navigate(`/enquiry/${enquiryId}/quote`)}
                    className="w-full mt-4 md:mt-6 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                  >
                    View Full Quote
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                
                {/* Quote Outcome Section - Only show if quote exists and is sent/approved */}
                {(quote.status === 'sent' || quote.status === 'approved' || enquiry?.status === 'quoted' || enquiry?.status === 'won' || enquiry?.status === 'lost') && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 overflow-hidden">
                    <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
                      Quote Outcome
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Track whether this quote was won or lost
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        onClick={() => updateQuoteOutcome('pending')}
                        variant={enquiry?.quote_outcome === 'pending' || !enquiry?.quote_outcome ? 'default' : 'outline'}
                        className={`rounded-xl ${
                          enquiry?.quote_outcome === 'pending' || !enquiry?.quote_outcome
                            ? 'bg-amber-500 hover:bg-amber-600 text-white'
                            : 'border-amber-200 text-amber-600 hover:bg-amber-50'
                        }`}
                        data-testid="outcome-pending"
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        Pending
                      </Button>
                      <Button
                        onClick={() => updateQuoteOutcome('won')}
                        variant={enquiry?.quote_outcome === 'won' ? 'default' : 'outline'}
                        className={`rounded-xl ${
                          enquiry?.quote_outcome === 'won'
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                            : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                        }`}
                        data-testid="outcome-won"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Won
                      </Button>
                      <Button
                        onClick={() => updateQuoteOutcome('lost')}
                        variant={enquiry?.quote_outcome === 'lost' ? 'default' : 'outline'}
                        className={`rounded-xl ${
                          enquiry?.quote_outcome === 'lost'
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'border-red-200 text-red-600 hover:bg-red-50'
                        }`}
                        data-testid="outcome-lost"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Lost
                      </Button>
                    </div>
                    {enquiry?.quote_outcome_notes && (
                      <p className="mt-3 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                        <span className="font-medium">Notes:</span> {enquiry.quote_outcome_notes}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : decision?.can_create_quote ? (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-700 mb-2">
                  Ready to Create Quote
                </h3>
                <p className="text-slate-500 mb-6">
                  The decision allows you to create a quote for this enquiry
                </p>
                <Button
                  onClick={() => navigate(`/enquiry/${enquiryId}/quote`)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Create Quote
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-700 mb-2">
                  Cannot Create Quote
                </h3>
                <p className="text-slate-500">
                  {decision 
                    ? 'The decision outcome does not allow quote creation'
                    : 'Run the decision engine first to determine if you should quote'}
                </p>
              </div>
            )}
          </TabsContent>
          
          {/* Attachments Tab */}
          <TabsContent value="attachments" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Document Attachments
                </h3>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  className="rounded-full bg-[#1e3a5f] hover:bg-[#162c46]"
                  data-testid="upload-attachment-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </Button>
              </div>
              
              {attachments.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                  <Paperclip className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-medium text-slate-700 mb-2">No attachments yet</h4>
                  <p className="text-sm text-slate-500 mb-4">
                    Upload project briefs, drawings, specifications, and other documents
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setShowUploadModal(true)}
                    className="rounded-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload First File
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Group by category */}
                  {['general', 'brief', 'programme', 'scope', 'drawing', 'specification', 'correspondence', 'tender', 'contract'].map(category => {
                    const categoryAttachments = attachments.filter(a => a.category === category);
                    if (categoryAttachments.length === 0) return null;
                    
                    const categoryLabels = {
                      general: 'General Documents',
                      brief: 'Project Brief',
                      programme: 'Programme / Schedule',
                      scope: 'Scope of Works',
                      drawing: 'Drawings & CAD',
                      specification: 'Specifications',
                      correspondence: 'Correspondence',
                      tender: 'Tender Documents',
                      contract: 'Contract Documents'
                    };
                    
                    return (
                      <div key={category} className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                          {categoryLabels[category]}
                        </h4>
                        <div className="space-y-2">
                          {categoryAttachments.map(attachment => (
                            <div
                              key={attachment.id}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  attachment.file_type?.includes('pdf') ? 'bg-red-100 text-red-600' :
                                  attachment.file_type?.includes('image') ? 'bg-blue-100 text-blue-600' :
                                  attachment.file_type?.includes('word') || attachment.file_type?.includes('document') ? 'bg-blue-100 text-blue-600' :
                                  attachment.file_type?.includes('excel') || attachment.file_type?.includes('sheet') ? 'bg-green-100 text-green-600' :
                                  attachment.file_type?.includes('dwg') || attachment.file_type?.includes('dxf') ? 'bg-purple-100 text-purple-600' :
                                  'bg-slate-200 text-slate-600'
                                }`}>
                                  {attachment.file_type?.includes('image') ? (
                                    <Image className="w-5 h-5" />
                                  ) : (
                                    <FileIcon className="w-5 h-5" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">{attachment.filename}</p>
                                  <p className="text-xs text-slate-500">
                                    {(attachment.file_size / 1024).toFixed(1)} KB • 
                                    Uploaded by {attachment.uploaded_by_name} • 
                                    {new Date(attachment.created_at).toLocaleDateString('en-GB')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="rounded-full"
                                  data-testid={`download-attachment-${attachment.id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {(user?.role === 'director' || attachment.uploaded_by === user?.id) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteAttachment(attachment.id)}
                                    className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-attachment-${attachment.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Summary Modal - Enhanced Readable Version */}
        <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
          <DialogContent className="w-[calc(100vw-16px)] max-w-3xl max-h-[90dvh] p-0 overflow-hidden bg-white md:w-auto">
            {/* Header */}
            <div className="bg-[#1e3a5f] px-4 md:px-6 py-4 md:py-5">
              <div className="flex items-start justify-between gap-2">
                <DialogHeader className="flex-1 min-w-0">
                  <DialogTitle className="font-heading text-base md:text-xl text-white flex items-center gap-2 md:gap-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-sm md:text-base truncate">Enquiry Summary</span>
                      <span className="text-xs md:text-sm font-normal text-white/70 truncate block">{enquiry?.reference_number}</span>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copySummaryToClipboard}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 md:h-9 md:w-auto md:px-3 p-0"
                  >
                    <Copy className="w-4 h-4 md:mr-1" />
                    <span className="hidden md:inline text-xs">Copy</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exportSummaryToPDF}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 md:h-9 md:w-auto md:px-3 p-0"
                  >
                    <Download className="w-4 h-4 md:mr-1" />
                    <span className="hidden md:inline text-xs">PDF</span>
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-4 md:p-6 overflow-y-auto max-h-[60vh] bg-slate-50">
              {summaryData && (
                <div className="space-y-6">
                  {/* Project Title Banner */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                    <h2 className="font-heading text-2xl font-bold text-[#1e3a5f] mb-2">
                      {enquiry?.project_name}
                    </h2>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {enquiry?.client_company || enquiry?.client_name}
                      </span>
                      {enquiry?.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {enquiry.location}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Status</p>
                      <p className="font-bold text-lg text-[#1e3a5f]">{summaryData.enquiry?.status}</p>
                    </div>
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Value</p>
                      <p className="font-bold text-lg text-[#1e3a5f]">{summaryData.enquiry?.estimated_value}</p>
                    </div>
                    {summaryData.decision && (
                      <>
                        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Decision</p>
                          <p className="font-bold text-lg text-[#1e3a5f]">{summaryData.decision.outcome}</p>
                        </div>
                        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Score</p>
                          <p className="font-bold text-lg text-[#1e3a5f]">{summaryData.decision.score}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Outstanding Info */}
                  {summaryData.outstanding_info?.length > 0 && (
                    <div className="bg-amber-50 p-5 rounded-2xl border border-amber-200">
                      <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2 text-base">
                        <AlertCircle className="w-5 h-5" />
                        Outstanding Information
                      </h4>
                      <ul className="space-y-2">
                        {summaryData.outstanding_info.map((info, idx) => (
                          <li key={idx} className="text-amber-700 flex items-start gap-3">
                            <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0 mt-2" />
                            <span className="text-sm">{info}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Next Steps */}
                  {summaryData.next_steps?.length > 0 && (
                    <div className="bg-sky-50 p-5 rounded-2xl border border-sky-200">
                      <h4 className="font-semibold text-sky-800 mb-3 flex items-center gap-2 text-base">
                        <ChevronRight className="w-5 h-5" />
                        Next Steps
                      </h4>
                      <ul className="space-y-2">
                        {summaryData.next_steps.map((step, idx) => (
                          <li key={idx} className="text-sky-700 flex items-start gap-3">
                            <span className="w-2 h-2 bg-sky-500 rounded-full flex-shrink-0 mt-2" />
                            <span className="text-sm">{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Quote Info */}
                  {summaryData.quote && (
                    <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200">
                      <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2 text-base">
                        <FileText className="w-5 h-5" />
                        Quote Created
                      </h4>
                      <div className="flex flex-wrap gap-6 text-sm">
                        <div>
                          <span className="text-emerald-600">Number:</span>
                          <span className="ml-2 font-semibold text-emerald-800">{summaryData.quote.quote_number}</span>
                        </div>
                        <div>
                          <span className="text-emerald-600">Total:</span>
                          <span className="ml-2 font-bold text-emerald-800">{summaryData.quote.total}</span>
                        </div>
                        <div>
                          <span className="text-emerald-600">Status:</span>
                          <span className="ml-2 font-semibold text-emerald-800">{summaryData.quote.status}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  {summaryData.timeline?.length > 0 && (
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                      <h4 className="font-semibold text-[#1e3a5f] mb-4 text-base">Recent Activity</h4>
                      <div className="space-y-3">
                        {summaryData.timeline.map((event, idx) => (
                          <div key={idx} className="flex items-center gap-4 text-sm">
                            <div className="w-3 h-3 bg-[#1e3a5f] rounded-full flex-shrink-0" />
                            <span className="text-slate-700 flex-1">{event.action}</span>
                            <span className="text-slate-400 text-xs font-mono">
                              {new Date(event.date).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Full Text Summary */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-semibold text-[#1e3a5f] mb-4 text-base">Full Summary</h4>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                        {summaryData.text_summary}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSummaryModal(false)}
                className="rounded-full"
              >
                Close
              </Button>
              <Button
                onClick={exportSummaryToPDF}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Decision Confirmation Modal */}
        <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-amber-500" />
                Reset Decision
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> This will delete the existing decision and allow you to re-run the decision engine with updated information.
                </p>
              </div>
              
              <p className="text-slate-600">
                Are you sure you want to reset the decision for <strong>{enquiry?.project_name}</strong>?
              </p>
              
              {quote && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-800">
                    <strong>Cannot reset:</strong> A quote has already been created for this enquiry. Delete the quote first to reset the decision.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowResetModal(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={resetDecision}
                disabled={resetLoading || !!quote}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              >
                {resetLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Reset Decision
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Enquiry Confirmation Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Delete Enquiry
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This action cannot be undone. All associated data including decisions will be permanently deleted.
                </p>
              </div>
              
              <p className="text-slate-600">
                Are you sure you want to delete <strong>{enquiry?.project_name}</strong> ({enquiry?.reference_number})?
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={deleteEnquiry}
                disabled={deleteLoading}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full"
              >
                {deleteLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete Enquiry
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Upload Attachment Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload Document
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category
                </label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                  data-testid="upload-category-select"
                >
                  <option value="general">General Documents</option>
                  <option value="brief">Project Brief</option>
                  <option value="programme">Programme / Schedule</option>
                  <option value="scope">Scope of Works</option>
                  <option value="drawing">Drawings & CAD</option>
                  <option value="specification">Specifications</option>
                  <option value="correspondence">Correspondence</option>
                  <option value="tender">Tender Documents</option>
                  <option value="contract">Contract Documents</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  File
                </label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-[#1e3a5f] transition-colors">
                  <input
                    type="file"
                    onChange={handleUploadAttachment}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.dwg,.dxf,.txt,.csv,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/*,text/plain,text/csv,application/zip"
                    disabled={uploadLoading}
                    data-testid="file-upload-input"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer"
                  >
                    {uploadLoading ? (
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mb-2" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                        <span className="text-sm font-medium text-[#1e3a5f]">Click to select file</span>
                        <p className="text-xs text-slate-500 mt-1">
                          PDF, DOC, XLS, PPT, Images, CAD files (max 10MB)
                        </p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowUploadModal(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Edit Enquiry Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Update Enquiry Details
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-800">
                      Update the missing information below. Fields marked with <span className="font-semibold">★</span> affect the decision engine.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Client Information */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                  <User className="w-4 h-4" /> Client Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Contact Name</Label>
                    <Input
                      value={editForm.client_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="John Smith"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Company</Label>
                    <Input
                      value={editForm.client_company}
                      onChange={(e) => setEditForm(prev => ({ ...prev, client_company: e.target.value }))}
                      placeholder="Acme Construction"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Email</Label>
                    <Input
                      type="email"
                      value={editForm.client_email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="john@company.com"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Phone</Label>
                    <Input
                      value={editForm.client_phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="+44 20 1234 5678"
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </div>
              
              {/* Client Type - Key Decision Field */}
              <div className="space-y-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h4 className="font-medium text-amber-800 flex items-center gap-2">
                  <Target className="w-4 h-4" /> Client Type ★
                </h4>
                <p className="text-xs text-amber-700 mb-2">
                  Only these client types can proceed to Quote
                </p>
                <RadioGroup
                  value={editForm.client_type}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, client_type: v }))}
                  className="grid grid-cols-2 gap-3"
                >
                  {[
                    { id: 'contractor', label: 'Main Contractor' },
                    { id: 'project_manager', label: 'Project Manager' },
                    { id: 'quantity_surveyor', label: 'Quantity Surveyor' },
                    { id: 'developer', label: 'Developer' },
                    { id: 'landlord', label: 'Landlord' },
                    { id: 'architect', label: 'Architect' }
                  ].map(type => (
                    <div key={type.id} className="flex items-center space-x-2">
                      <RadioGroupItem value={type.id} id={`client-type-${type.id}`} />
                      <Label htmlFor={`client-type-${type.id}`} className="text-sm cursor-pointer">
                        {type.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                
                <h4 className="font-medium text-amber-800 flex items-center gap-2 mt-4">
                  Delivery Authority ★
                </h4>
                <RadioGroup
                  value={editForm.delivery_authority}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, delivery_authority: v }))}
                  className="grid grid-cols-1 gap-3"
                >
                  {[
                    { id: 'confirmed', label: 'Confirmed - Has authority to proceed' },
                    { id: 'likely', label: 'Likely - Expected to have authority' },
                    { id: 'unknown', label: 'Unknown - Not yet confirmed' }
                  ].map(type => (
                    <div key={type.id} className="flex items-center space-x-2">
                      <RadioGroupItem value={type.id} id={`delivery-${type.id}`} />
                      <Label htmlFor={`delivery-${type.id}`} className="text-sm cursor-pointer">
                        {type.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              
              {/* Project Clarity - Key Decision Fields */}
              <div className="space-y-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h4 className="font-medium text-amber-800 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Project Clarity ★
                </h4>
                
                <div className="space-y-2">
                  <Label className="text-slate-600">Decision Maker Name ★</Label>
                  <Input
                    value={editForm.decision_maker_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, decision_maker_name: e.target.value }))}
                    placeholder="Name of the person who can sign off"
                    className="rounded-xl bg-white"
                  />
                </div>
                
                <div className="space-y-3 mt-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="has_programme"
                      checked={editForm.has_programme_summary}
                      onCheckedChange={(v) => setEditForm(prev => ({ ...prev, has_programme_summary: v }))}
                    />
                    <Label htmlFor="has_programme" className="text-sm cursor-pointer">
                      Programme / Schedule Available ★
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="has_scope"
                      checked={editForm.has_scope_summary}
                      onCheckedChange={(v) => setEditForm(prev => ({ ...prev, has_scope_summary: v }))}
                    />
                    <Label htmlFor="has_scope" className="text-sm cursor-pointer">
                      Scope of Works Document Available ★
                    </Label>
                  </div>
                </div>
              </div>
              
              {/* Project Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Project Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Project Name</Label>
                    <Input
                      value={editForm.project_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, project_name: e.target.value }))}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Location</Label>
                    <Input
                      value={editForm.location}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Estimated Value (£)</Label>
                  <Input
                    type="number"
                    value={editForm.estimated_value}
                    onChange={(e) => setEditForm(prev => ({ ...prev, estimated_value: parseFloat(e.target.value) || '' }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleEditSubmit(false)}
                  disabled={editLoading}
                  variant="outline"
                  className="rounded-xl"
                >
                  {editLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Only
                </Button>
                <Button
                  onClick={() => handleEditSubmit(true)}
                  disabled={editLoading}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                >
                  {editLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  Save & Re-run Decision
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
